/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import clsx from "clsx";
import React, { ReactNode, RefObject, forwardRef, useRef } from "react";
import { createPortal } from "react-dom";

import { Backdrop } from "../Backdrop/Backdrop";
import { useCombineRefs } from "../hooks";
import { useDrawer } from "./useDrawer";

const placementLeft = "left";
const placementRight = "right";

type DrawerProps = {
  children: ReactNode;
  className?: string;
  onClose?: () => void;
  placement: typeof placementLeft | typeof placementRight;
  producerRef?: RefObject<HTMLElement>;
  role?: string;
  visible?: boolean;
};

const getPlacementClass = (placement: DrawerProps["placement"]): string => {
  return {
    [placementLeft]: "tw-drawer--left",
    [placementRight]: "tw-drawer--right",
  }[placement];
};

export const Drawer = forwardRef<HTMLElement, DrawerProps>(
  (
    {
      children,
      className,
      onClose,
      placement,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      producerRef,
      role = "dialog",
      visible = false,
      ...props
    },
    forwardedRef
  ) => {
    const ownRef = useRef<HTMLElement>(null);
    const ref = useCombineRefs(forwardedRef, ownRef);
    const { isHidden, portalNode, setIsHidden } = useDrawer({
      onClose,
      ref,
      visible,
    });
    const component = (
      <>
        <Backdrop
          onHide={(): void => {
            setIsHidden(true);
          }}
          onShow={(): void => {
            setIsHidden(false);
          }}
          visible={visible}
        />
        <nav
          {...props}
          className={clsx(
            "tw-drawer",
            className,
            getPlacementClass(placement),
            isHidden && "tw-drawer--none",
            visible ? "tw-drawer--visible" : "tw-drawer--hidden"
          )}
          ref={ref}
          role={role}
        >
          {children}
        </nav>
      </>
    );
    return portalNode ? createPortal(component, portalNode) : component;
  }
);
